<template>
  <div class="relative sort-filter-dropdown flex-shrink-0">
    <button ref="buttonEl" type="button" @click="toggleOpen" @keydown="handleKeydown"
      class="h-full flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 text-sm border rounded-lg transition-colors cursor-pointer text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"/>
      </svg>
      <span class="whitespace-nowrap">{{ activeLabel }}</span>
      <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform" :class="open ? 'rotate-180' : ''"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
      </svg>
    </button>

    <!-- Teleported + fixed for the same reason as CategoryFilterButton's own
         dropdown: escapes this panel's scroll/overflow context, and the same
         right-edge overflow guard since this button can also end up sitting
         near the edge of a narrow viewport. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="open = false"></div>
      <div v-if="open" :style="panelStyle"
        class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5 max-h-80 overflow-y-auto">
        <button v-for="(option, index) in options" :key="option.value" type="button"
          :ref="(el) => setOptionRef(el, index)"
          @click="selectOption(option)" @mouseenter="activeIndex = index"
          :class="index === activeIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
          class="w-full flex items-center justify-between gap-2.5 text-left px-3.5 py-2 text-sm transition-colors cursor-pointer">
          <span :class="isSelected(option) ? 'font-medium text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'">
            {{ option.label }}
          </span>
          <svg v-if="isSelected(option)" class="w-3.5 h-3.5 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
          </svg>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  // [{ value, label, by, order }] — see itComponentOptions.js's SORT_OPTIONS.
  options: { type: Array, required: true },
  // The active option's own `value` (not `by`/`order` separately) — simplest
  // single source of truth for "which row is currently checked".
  modelValue: { type: String, required: true },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const activeIndex = ref(-1)
const optionRefs = ref([])
const buttonEl = ref(null)
const panelStyle = ref({})

const activeLabel = computed(() => props.options.find((o) => o.value === props.modelValue)?.label || 'Sort')

function isSelected(option) {
  return option.value === props.modelValue
}

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el
}

function positionPanel() {
  const rect = buttonEl.value.getBoundingClientRect()
  const width = Math.max(rect.width, 220)
  const overflowsRight = rect.left + width > window.innerWidth - 8
  const left = overflowsRight ? Math.max(8, rect.right - width) : rect.left
  return {
    top: `${rect.bottom + 6}px`,
    left: `${left}px`,
    width: `${width}px`,
  }
}

function toggleOpen() {
  const opening = !open.value
  if (opening) panelStyle.value = positionPanel()
  open.value = opening
  activeIndex.value = -1
}

function selectOption(option) {
  open.value = false
  emit('update:modelValue', option.value)
}

watch(() => props.options, () => {
  activeIndex.value = -1
  optionRefs.value = []
})

watch(activeIndex, (index) => {
  if (index >= 0) optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function handleKeydown(e) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen()
    }
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (props.options.length) activeIndex.value = (activeIndex.value + 1) % props.options.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (props.options.length) {
      activeIndex.value = activeIndex.value <= 0 ? props.options.length - 1 : activeIndex.value - 1
    }
  } else if (e.key === 'Escape') {
    open.value = false
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    selectOption(props.options[activeIndex.value])
  }
}
</script>
